import Model, { attr, belongsTo, type AsyncBelongsTo } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import type CompanyModel from './company';

export type TasDispatchState = 'CREATED' | 'AUTHORIZED' | 'DISBURSING' | 'DISBURSED' | 'CANCELED';

/** Number of unique authorizers required before a dispatch can be disbursed */
const REQUIRED_AUTHORIZATIONS = 2;

export default class TasDispatchModel extends Model {
  @attr declare description: string;
  @attr declare state: TasDispatchState;
  @attr declare amount: number;
  @attr declare dispatchDate: string;
  @attr declare authorizedAt: string | null;
  @attr declare disbursedAt: string | null;
  @attr declare authorizerIds: number[];
  @attr declare metadata: Record<string, unknown>;

  // Computed attributes from backend
  @attr declare authorizationCount: number;
  @attr declare canAuthorize: boolean;
  @attr declare canDisburse: boolean;
  @attr declare canCancel: boolean;

  // Relationships
  @belongsTo('company', { async: true, inverse: null })
  declare company: AsyncBelongsTo<CompanyModel>;

  get isCreated() {
    return this.state === 'CREATED';
  }

  get isAuthorized() {
    return this.state === 'AUTHORIZED';
  }

  get isDisbursing() {
    return this.state === 'DISBURSING';
  }

  get isDisbursed() {
    return this.state === 'DISBURSED';
  }

  get isCanceled() {
    return this.state === 'CANCELED';
  }

  get authorizationProgress() {
    return `${this.authorizationCount}/${REQUIRED_AUTHORIZATIONS}`;
  }

  get needsSecondAuthorizer() {
    return this.authorizationCount === REQUIRED_AUTHORIZATIONS - 1;
  }

  get fullyAuthorized() {
    return this.authorizationCount >= REQUIRED_AUTHORIZATIONS;
  }

  [Type] = 'tas-dispatch' as const;
}
